/**
 * @fileoverview Pseudo-localization — mechanically derive a QA locale's bundle
 * from the source-language bundle.
 *
 * AAS-I18N-002 ruling 11 (2026-08-10).
 *
 * ## Why this exists at all
 *
 * Registering page-shell's `en-XA` bundle is only half of a working
 * pseudo-locale. i18next picks `resolvedLanguage` by walking `languages` and
 * stopping at the first one that has a bundle **in the default namespace**
 * (`translation`). page-shell's bundle lives in the `shell` namespace, so an
 * app booted with `lng: 'en-XA'` and `resources: { en, es }` resolved back
 * to `'en'` — MEASURED, not predicted: the first version of the ruling-11
 * change did exactly that, and `useSyncHtmlLang` (which reads
 * `resolvedLanguage` first) would then have written `lang="en" dir="ltr"` and
 * made the whole pseudo-locale inert again, silently.
 *
 * Deriving the app half from the source bundle fixes that AND is what a
 * pseudo-locale is for in the first place — Microsoft's `en-XA` and Google's
 * `ar-XB` are both mechanical transforms of English, never hand-authored:
 *
 *   · every leaf carries a visible MARKER, so a string rendering WITHOUT one did
 *     not come from a registered i18next bundle (RECIPE miss-classes 1–9 become
 *     visible by eye instead of by census);
 *   · the marker also lengthens every string, which is the cheap version of
 *     the ~20% expansion Spanish causes — buttons and table headers clip in QA
 *     rather than in production.
 *
 * page-shell's `en-XA` chrome bundle is derived by this same function (it was
 * hand-written until MUTATION-INSTRUMENT-001 B45, 2026-09-26, when one of its
 * leaves was found seven weeks stale), so shell chrome and app copy read as one
 * locale rather than two conventions.
 *
 * ⚠️ Interpolation is untouched. The transform only PREFIXES, so `{{count}}`,
 * `<0>…</0>` and i18next plural suffixes all survive verbatim. A transform
 * that wrapped or reversed text would corrupt them.
 *
 * ## 🔴 EXACTLY WHAT AN UNMARKED STRING MEANS (W9, ruling 28 — this used to overclaim)
 *
 * The bullet above originally read *"any string rendering WITHOUT one is hardcoded copy"*.
 * That was **false as written**, and measurably so: W7 counted **55 of 177** rendered strings
 * carrying the marker (**31%**), which makes "unmarked ⇒ hardcoded" ~97% false positives and
 * the signal worse than useless.
 *
 * Two different causes were folded into one claim, and only the first was a defect:
 *
 * 1. **A registered i18next NAMESPACE with no `en-XA` bundle.** `login`, `onboarding` and
 *    `captcha` ship `en` + `es` only, so under `en-XA` they fell back to English and rendered
 *    unmarked *even though every one of those strings is fully catalogued*. **FIXED** —
 *    `createAppI18n.installPseudoNamespaces` now derives a pseudo bundle for **every**
 *    namespace on the instance, at boot, on `changeLanguage`, and on the store's `added`
 *    event (for namespaces registered lazily by the surface that mounts them).
 *    Measured on a real instance: **86.5% → 100.0%** of 1,245 catalog leaves.
 *
 * 2. **Strings that were never in an i18next bundle at all.** These are still unmarked and
 *    *should* be — but "unmarked" splits three ways, and only the third is a defect:
 *      · **server data** — tenant, course, employee and product names, API error text. Cannot
 *        and must not be marked; it is content, not copy.
 *      · **per-language DATA MAPS outside i18next** — chiefly
 *        `@puffin-core/brand-assets`' `resolveBrandTagline`, whose `language.split('-')[0]`
 *        normalises `en-XA` to `en`. **12 taglines**, one per brand, render unmarked by
 *        construction. Not fixed here: it would mean teaching a dependency-free data package
 *        about pseudo-locales for twelve strings.
 *      · **genuinely hardcoded copy** — the signal this whole mechanism exists to produce.
 *
 * So the honest reading of a bare page in `en-XA` is: *an unmarked string is either server
 * data, a brand tagline, or a defect* — and the first two are enumerable, which is what makes
 * the third findable by eye.
 *
 * @module @puffin-core/i18n-bootstrap/pseudoLocale
 */

/**
 * The marker prefixed to every derived leaf, `@puffin-core/page-shell`'s
 * `en-XA` chrome bundle included.
 *
 * @type {string}
 */
export const PSEUDO_LOCALE_MARKER = '[RTL] ';

/**
 * Recursively derive a pseudo-localized copy of a translation bundle.
 *
 * Non-string leaves (numbers, booleans, null) are passed through unchanged —
 * a catalog should not contain them, but corrupting one into `"[RTL] 3"` would
 * be worse than leaving it.
 *
 * @param {*} bundle - A nested translation object, an array, or a leaf.
 * @param {string} [marker] - Prefix applied to every string leaf.
 * @returns {*} A new structure of the same shape; the input is never mutated.
 */
export function pseudoLocalizeBundle(bundle, marker = PSEUDO_LOCALE_MARKER) {
  if (typeof bundle === 'string') {
    return `${marker}${bundle}`;
  }
  if (Array.isArray(bundle)) {
    return bundle.map((entry) => pseudoLocalizeBundle(entry, marker));
  }
  if (bundle !== null && typeof bundle === 'object') {
    const out = {};
    for (const [key, value] of Object.entries(bundle)) {
      out[key] = pseudoLocalizeBundle(value, marker);
    }
    return out;
  }
  return bundle;
}
